import _extends from "@babel/runtime/helpers/extends";
var _CloseIcon;
import React, { useCallback, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { RemoveScroll } from 'react-remove-scroll';
import { BLACK_OVERLAY, NIGHTINGALE_BLACK, SENSATION_WHITE, BLACK_OVERLAY_OPACITY_93 } from '../colors';
import { SUPER_SHADOW } from '../shadows';
import media from '../media';
import { zIndexValue as zIndexPosition } from '../helpers/position';
import { ModalContentItemContext } from './context';
import CloseIcon from '@tourlane/iconography/Glyphs/Navigation/Close';
import { IconButton } from '../Button';
import { MODAL_CLOSE_BUTTON_SIZE, MODAL_CLOSE_CIRCLE_BUTTON_PADDING, MODAL_WIDTH } from './constants';
import CircleIconButton from '../Button/CircleIconButton';
import { useDidUpdateEffect, useMediaBreakpoints } from '../hooks';
import { EMPTY_PROPS } from '../helpers/types';
import { getMs } from '../helpers/common';
import { isDefined } from '../helpers/defined';
import { fullSpaceFixedStyle } from '../tokens/full-space';
import { overflowHiddenStyle, overflowStyle } from '../tokens/overflow';
import { backgroundClipStyle } from '../tokens/background';
import { width } from '../style/width';
import { minHeight } from '../style/min-height';
import { padding } from '../style/padding';
const IS_MOBILE = {
  xs: true,
  sm: false
};
const MODAL_ANIMATION_DURATION = 250;
const StyledWrapper = styled.div`
  position: relative;
  ${({
  $fullScreen,
  $isMobile
}) => $fullScreen || $isMobile ? `
        min-height: 100%;
      ` : ''}
`;
const RemoveScrollWrapper = styled(RemoveScroll)`
  height: 100%;
`;
const StyledModal = styled.div`
  position: ${({
  $fullScreen,
  $isMobile
}) => $fullScreen || $isMobile ? 'absolute' : 'relative'};
  background-color: ${({
  $transparent
}) => $transparent ? 'transparent' : SENSATION_WHITE};
  display: flex;
  flex-direction: column;
  overflow: ${({
  $withOverflowHidden
}) => $withOverflowHidden ? 'hidden' : 'visible'};

  width: ${({
  $fullScreen,
  $isMobile
}) => $fullScreen || $isMobile ? '100%' : '100vw'};
  height: ${({
  $fullScreen,
  $isMobile
}) => $fullScreen || $isMobile ? '100%' : '100vh'};

  box-shadow: none;
  border-radius: 0;

  ${overflowStyle}
  ${({
  $withOverflowHidden
}) => $withOverflowHidden && `
    ${backgroundClipStyle}
    ${overflowHiddenStyle}
  `}
  
  ${({
  $fullScreen,
  $maxHeightPercent,
  $hasClose,
  $width
}) => !$fullScreen && media.from.sm`
      border-radius: 4px;
      box-shadow: ${SUPER_SHADOW};
      height: auto;
      max-height: ${`${$maxHeightPercent}vh`};
      ${$hasClose ? minHeight(MODAL_CLOSE_BUTTON_SIZE) : ''}
      ${width($width)}
    `}
`;
const StyledCloseCircleButton = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  ${padding(MODAL_CLOSE_CIRCLE_BUTTON_PADDING)}
`;
const StyledCloseButton = styled(IconButton)`
  position: absolute;
  right: 0;
  top: 0;
`;

// According to: https://github.com/reactjs/react-modal/issues/603#issuecomment-433210653
const ReactModalAdapter = ({
  className,
  ...props
}) => /*#__PURE__*/React.createElement(ReactModal, _extends({
  portalClassName: className,
  className: `${className}__content`,
  overlayClassName: `${className}__overlay`
}, props));
const StyledReactModal = styled(ReactModalAdapter)`
  & {
    ${({
  $zIndex
}) => isDefined($zIndex) ? `z-index: ${$zIndex};` : ''}
  }
  &__overlay {
    background-color: transparent;
    outline: none;
    ${media.from.sm`
      background-color: ${BLACK_OVERLAY_OPACITY_93};
    `}
    ${({
  $zIndex
}) => isDefined($zIndex) ? `z-index: ${$zIndex};` : ''}
    ${fullSpaceFixedStyle}
  }

  @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    &__overlay {
      -webkit-backdrop-filter: blur(10px);
      backdrop-filter: blur(10px);
      ${media.from.sm`
        background-color: ${BLACK_OVERLAY};
      `}
    }
  }

  &__content {
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    background-color: transparent;
    outline: none;
    padding: 0;
    border-radius: 0;
    border: none;
    position: absolute;
    height: 100%;
    transform: ${({
  $fullScreen,
  $isMobile
}) => !$fullScreen && !$isMobile ? 'translate(-50%, -50%)' : 'none'};
    ${({
  $fullScreen,
  $isMobile
}) => $fullScreen || $isMobile ? `
          top: auto;
          left: auto;
          right: auto;
          bottom: auto;
          overflow: auto;
          overflowY: scroll;
          position: static;
          height: 100%;
          width: 100%;
        ` : media.from.sm`
          height: auto;
        `}
    & > div {
      overflow: hidden;
    }
  }

  /* Desktop animation */
  ${({
  $animate
}) => $animate ? media.from.sm`
        &__overlay {
          background-color: rgba(0, 0, 0, 0);
          transition: background-color ${getMs(MODAL_ANIMATION_DURATION)} ease-out;
          
          &.ReactModal__Overlay--after-open {
            background-color: ${BLACK_OVERLAY_OPACITY_93};
          }
          &.ReactModal__Overlay--before-close {
            background-color: rgba(0, 0, 0, 0);
          }
        }

        @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
          &__overlay {
            &.ReactModal__Overlay--after-open {
              background-color: ${BLACK_OVERLAY};
            }
          }
        }
        
        &__content {
          & > div {
            opacity: 0;
            transform: translateY(-64px);
            transition: transform ${getMs(MODAL_ANIMATION_DURATION)} ease-out 0.05s,
              opacity ${getMs(MODAL_ANIMATION_DURATION)} ease-out 0.05s;
          }
          &.ReactModal__Content--after-open > div {
            opacity: 1;
            transform: translateY(0);
          }
          &.ReactModal__Content--before-close > div {
            opacity: 0;
            transform: translateY(-64px);
          }
        }
      ` : ''}

  /* Mobile animation */
  ${({
  $animate
}) => $animate ? media.to.xs`
        &__content {
          & > div {
            opacity: 0;
            transform: translateY(-48px);
            transition: transform ${getMs(MODAL_ANIMATION_DURATION)} ease-out 0.05s,
              opacity ${getMs(MODAL_ANIMATION_DURATION)} ease-out 0.05s;
          }
          &.ReactModal__Content--after-open > div {
            opacity: 1;
            transform: translateY(0);
          }
          &.ReactModal__Content--before-close > div {
            opacity: 0;
            transform: translateY(-48px);
          }
        }
      ` : ''}
`;
const Modal = ({
  fullScreen = false,
  withOverflowHidden = false,
  transparent = false,
  animate = true,
  isOpen = false,
  hasClose = true,
  parentQuerySelector,
  parentElement,
  onClose,
  className = '',
  contentLabel = '',
  width = MODAL_WIDTH,
  maxHeightPercent = 94,
  zIndex = zIndexPosition('modal'),
  overlayStyle = EMPTY_PROPS,
  contentStyle = EMPTY_PROPS,
  noLockBodyScroll = false,
  children,
  onOpened,
  onClosed,
  onToggled,
  closeButtonVariant = 'default',
  closeButtonLabel = 'Close',
  closeButtonProps = EMPTY_PROPS,
  ...props
}) => {
  const [shouldRemoveScrollbar, setShouldRemoveScrollbar] = useState(false);
  useDidUpdateEffect(() => {
    const notify = () => {
      onToggled?.(isOpen);
      if (isOpen) {
        onOpened?.();
      } else {
        onClosed?.();
      }
    };
    if (animate) {
      const timeoutId = setTimeout(notify, MODAL_ANIMATION_DURATION);
      return () => clearTimeout(timeoutId);
    } else {
      notify();
    }
  }, [isOpen]);
  useDidUpdateEffect(() => {
    if (noLockBodyScroll) return;
    const timeout = setTimeout(() => {
      setShouldRemoveScrollbar(isOpen);
    }, !isOpen ? MODAL_ANIMATION_DURATION : 0);
    return () => clearTimeout(timeout);
  }, [isOpen, noLockBodyScroll]);
  const isMobile = useMediaBreakpoints(IS_MOBILE);
  const childrenArray = useMemo(() => React.Children.toArray(children), [children]);
  const modalStyle = useMemo(() => ({
    overlay: overlayStyle,
    content: contentStyle
  }), [overlayStyle, contentStyle]);
  const parentSelector = useCallback(() => parentElement || (parentQuerySelector ? document.querySelector(parentQuerySelector) : document.body), [parentElement, parentQuerySelector]);
  return /*#__PURE__*/React.createElement(StyledReactModal, _extends({
    ariaHideApp: false,
    style: modalStyle,
    closeTimeoutMS: animate ? MODAL_ANIMATION_DURATION : 0,
    parentSelector: parentSelector,
    isOpen: isOpen,
    onRequestClose: onClose,
    contentLabel: contentLabel,
    $animate: animate,
    $fullScreen: fullScreen,
    $isMobile: isMobile,
    $zIndex: zIndex
  }, props), /*#__PURE__*/React.createElement(RemoveScrollWrapper, {
    enabled: shouldRemoveScrollbar
  }, /*#__PURE__*/React.createElement(StyledWrapper, {
    $fullScreen: fullScreen,
    $isMobile: isMobile
  }, /*#__PURE__*/React.createElement(StyledModal, {
    $hasClose: hasClose,
    $fullScreen: fullScreen,
    $isMobile: isMobile,
    $width: width,
    $maxHeightPercent: maxHeightPercent,
    $withOverflowHidden: withOverflowHidden,
    $transparent: transparent,
    className: className
  }, childrenArray.map((element, index) => {
    const contextItemValue = {
      index,
      first: index === 0,
      last: index === childrenArray.length - 1
    };
    return /*#__PURE__*/React.createElement(ModalContentItemContext.Provider, {
      value: contextItemValue,
      key: index
    }, element);
  })), hasClose && (closeButtonVariant === 'default' ? /*#__PURE__*/React.createElement(StyledCloseButton, _extends({
    tabIndex: -1,
    role: "button",
    "aria-label": closeButtonLabel,
    icon: _CloseIcon || (_CloseIcon = /*#__PURE__*/React.createElement(CloseIcon, null)),
    onClick: onClose,
    size: MODAL_CLOSE_BUTTON_SIZE
  }, closeButtonProps)) : closeButtonVariant === 'circle' ? /*#__PURE__*/React.createElement(StyledCloseCircleButton, null, /*#__PURE__*/React.createElement(CircleIconButton, _extends({
    tabIndex: -1,
    role: "button",
    "aria-label": closeButtonLabel,
    hasClose: true,
    onClick: onClose,
    iconColor: NIGHTINGALE_BLACK
  }, closeButtonProps))) : null))));
};
export default Modal;